import { useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'palette/Button';
import { Pagination as GenericPagination } from 'palette/Pagination';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { setQuery } from '@common/Listing/actions/listingActions';
import { usePaginationSeoLinks } from '@common/Listing/hooks';
import ListingContext from '@common/Listing/ListingContext';
import { ReactOnRailsRootComponent } from '@common/ReactOnRails';
import { dispatchGaPageviewEvent } from '@common/Tracking';
import { BEMBlock } from '@helpers/classnames';
import Dropdown from './Dropdown';
import '../styles/Pagination.scss';

// TODO: clean this component once feature flag is removed
export function Pagination({ onChange }) {
    const { hasProducts } = useContext(ListingContext);
    const translate = useTranslation();
    const dispatch = useDispatch();
    const { currentPage, pageCount } = useSelector(state => state.pagination);
    const config = useConfig();
    const [next, prev, dropdown] = usePaginationSeoLinks(currentPage, pageCount);

    const isSeoPaginationEnabled = config.isActiveFeature('PLT-33020-seo-friendly-pagination');

    const [, BEMElement] = BEMBlock('listing-pagination');

    const handlePaginationChange = ({ nextPageIndex: page }) => {
        const isValidPage = page >= 1 && page <= pageCount && page !== currentPage;

        if (isValidPage) {
            dispatch(setQuery(page));
            onChange?.();
            dispatchGaPageviewEvent();
        }
    };

    if (pageCount === 1 || !hasProducts) {
        return null;
    }

    if (isSeoPaginationEnabled) {
        return (
            <div data-testid="listPagination">
                <GenericPagination
                    isCustomStyle
                    buttonClassName={BEMElement('btn')}
                    currentPage={currentPage}
                    dropdownLabel={translate('listing.paging.page.label')}
                    hrefDropdown={dropdown}
                    hrefNext={next}
                    hrefPrev={prev}
                    labelNext={translate('listing.paging.next.link')}
                    labelPrevious={translate('listing.paging.previous.link')}
                    pageCount={pageCount}
                    onChange={handlePaginationChange}
                />
            </div>
        );
    }

    return (
        <div className="pagination">
            <Button
                className="pagination__btn"
                disabled={currentPage === 1}
                label={translate('listing.paging.previous.link')}
                size="medium"
                theme="secondary"
                onClick={() => handlePaginationChange({ nextPageIndex: currentPage - 1 })}
            />
            <div className="pagination__dropdown">
                <Dropdown
                    customStyle
                    currentPage={currentPage}
                    pageCount={pageCount}
                    onChange={nextPageIndex => handlePaginationChange({ nextPageIndex })}
                />
            </div>
            <Button
                className="pagination__btn"
                disabled={currentPage === pageCount}
                label={translate('listing.paging.next.link')}
                size="medium"
                theme="secondary"
                onClick={() => handlePaginationChange({ nextPageIndex: currentPage + 1 })}
            />
        </div>
    );
}

export const PaginationStandalone = ReactOnRailsRootComponent(Pagination, { withReduxStore: true });
